import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
	CreditCard,
	CheckCircle2,
	AlertTriangle,
	RefreshCw,
	Link2,
} from "lucide-react";

import { fetchAPI } from "../lib/utils";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

function ExternalPaymentLinkCard() {
	const { t } = useLanguage();
	const { user, updateUser } = useAuth();
	const [value, setValue] = useState(user?.external_payment_link || "");
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		setValue(user?.external_payment_link || "");
	}, [user?.external_payment_link]);

	const save = async () => {
		setSaving(true);
		try {
			const updated = await fetchAPI(`/users/${user.user_id}`, {
				method: "PUT",
				body: JSON.stringify({ external_payment_link: value.trim() || null }),
			});
			updateUser(updated);
			toast.success(t("externalLinkSaved"));
		} catch (err) {
			toast.error(err.detail || err.message);
		} finally {
			setSaving(false);
		}
	};

	return (
		<Card className="bg-card border-border rounded-sm">
			<CardContent className="p-6 space-y-3">
				<div className="flex items-center gap-2">
					<Link2 className="w-5 h-5 text-volt" />
					<h2 className="font-heading text-xl">{t("externalPaymentLink")}</h2>
				</div>
				<p className="text-sm text-muted-foreground">
					{t("externalPaymentLinkIntro")}
				</p>
				<div className="space-y-1.5">
					<Label htmlFor="external-payment-link">
						{t("externalPaymentLinkLabel")}
					</Label>
					<Input
						id="external-payment-link"
						className="rounded-sm"
						placeholder="https://buy.stripe.com/... · https://paypal.me/..."
						value={value}
						onChange={(e) => setValue(e.target.value)}
					/>
				</div>
				<Button
					className="bg-volt hover:bg-volt/90 rounded-sm"
					disabled={saving}
					onClick={save}
				>
					{t("save")}
				</Button>
			</CardContent>
		</Card>
	);
}

export function StripeConnectPanel() {
	const { t } = useLanguage();
	const [status, setStatus] = useState(null);
	const [loading, setLoading] = useState(true);
	const [busy, setBusy] = useState(false);

	const load = useCallback(async () => {
		setLoading(true);
		try {
			const data = await fetchAPI("/billing/connect/status");
			setStatus(data);
		} catch (err) {
			toast.error(err.detail || err.message);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		load();
	}, [load]);

	const onboard = async () => {
		setBusy(true);
		try {
			const res = await fetchAPI("/billing/connect/onboard", {
				method: "POST",
			});
			if (res?.onboarding_url) {
				window.location.href = res.onboarding_url; // hosted Stripe onboarding
			}
		} catch (err) {
			toast.error(err.detail || err.message);
			setBusy(false);
		}
	};

	const refresh = async () => {
		setBusy(true);
		try {
			const data = await fetchAPI("/billing/connect/refresh", {
				method: "POST",
			});
			setStatus(data);
			toast.success(t("statusUpdated"));
		} catch (err) {
			toast.error(err.detail || err.message);
		} finally {
			setBusy(false);
		}
	};

	if (loading) {
		return <p className="text-sm text-muted-foreground">{t("loading")}</p>;
	}

	// Platform hasn't configured Stripe keys yet: only the V1 external-link fallback works.
	if (!status?.stripe_enabled) {
		return (
			<div className="space-y-4">
				<ExternalPaymentLinkCard />
				<Card className="bg-card border-border rounded-sm">
					<CardContent className="p-6 space-y-3">
						<div className="flex items-center gap-2">
							<AlertTriangle className="w-5 h-5 text-acid" />
							<h2 className="font-heading text-xl">{t("onlinePayments")}</h2>
						</div>
						<p className="text-sm text-muted-foreground">
							{t("stripeNotConfigured")}
						</p>
					</CardContent>
				</Card>
			</div>
		);
	}

	const active = status.charges_enabled;
	const started = status.status && status.status !== "not_connected";

	return (
		<div className="space-y-4">
			<ExternalPaymentLinkCard />
			<Card className="bg-card border-border rounded-sm">
				<CardContent className="p-6 space-y-4">
					<div className="flex items-center justify-between">
						<div className="flex items-center gap-2">
							<CreditCard className="w-5 h-5 text-volt" />
							<h2 className="font-heading text-xl">{t("onlinePayments")}</h2>
						</div>
						{active ? (
							<Badge className="rounded-sm bg-volt/15 text-volt border border-volt/30">
								{t("connectActive")}
							</Badge>
						) : started ? (
							<Badge className="rounded-sm bg-acid/10 text-acid border border-acid/20">
								{t("connectOnboarding")}
							</Badge>
						) : (
							<Badge className="rounded-sm bg-muted text-muted-foreground">
								{t("connectNotConnected")}
							</Badge>
						)}
					</div>

					<p className="text-sm text-muted-foreground">
						{t("stripeConnectIntro")}
					</p>

					{active ? (
						<div className="flex items-center gap-2 text-sm text-volt">
							<CheckCircle2 className="w-4 h-4" />
							{t("connectActiveHint")}
						</div>
					) : (
						<div className="flex flex-wrap gap-2">
							<Button
								className="bg-volt hover:bg-volt/90 rounded-sm"
								disabled={busy}
								onClick={onboard}
							>
								<CreditCard className="w-4 h-4 mr-2" />
								{started ? t("completeSetup") : t("connectStripe")}
							</Button>
							{started && (
								<Button
									variant="outline"
									className="rounded-sm"
									disabled={busy}
									onClick={refresh}
								>
									<RefreshCw className="w-4 h-4 mr-2" />
									{t("refreshStatus")}
								</Button>
							)}
						</div>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
